import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { AppDispatch, RootState } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { deleteUserContent, getInstruments, patchContent } from '../../../features/sessions/sessionAudioContent/sliceUserContents';
import { Content, Instrument } from '../../../#interfaces/interfaces';
import { ContentPlayer } from '../../audioPlayer/WaveSurfer/contentPlayer/ContentPlayer';
import React from 'react';
import { toast } from 'react-toastify';
import { YesNoDialogModal } from '../dialogBox/group/yesNo/YesNoDialogModal';
import LoadingModal from '../loadingModal/LoadingModal';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// ── Design tokens ──────────────────────────────────────────────────────────────
const BG_DIALOG      = '#1c1c24';
const BG_INPUT       = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT_PRIMARY,
        bgcolor: BG_INPUT,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
    '& .MuiSelect-icon': { color: TEXT_SECONDARY },
};

// ── Interface ──────────────────────────────────────────────────────────────────
interface ContentShowEditProps {
    contentObj: Content | undefined;
    onCloseClick: (confirmation: boolean) => void;
}

// ── Componente ─────────────────────────────────────────────────────────────────
export default function ContentShowEdit({ contentObj, onCloseClick }: ContentShowEditProps) {
    const currentUser       = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders       = useSelector((state: RootState) => state.session.authHeaders);
    const errors            = useSelector((state: RootState) => state.sessionUserContents.errors);
    const dispatch          = useDispatch<AppDispatch>();
    const instrumentsForm   = useSelector((state: RootState) => state.sessionUserContents.instrumentsForm);
    const loadingUpdade     = useSelector((state: RootState) => state.sessionUserContents.loadingUpdate);
    const loadingDelete     = useSelector((state: RootState) => state.sessionUserContents.loadingDelete);

    const [titleForm, setTitleForm]                     = useState<string | undefined>(contentObj?.title);
    const [descriptionForm, setDescriptionForm]         = useState<string | undefined>(() => contentObj?.description ?? undefined);
    const [downloadAbledForm, setDownloadAbledForm]     = useState<boolean | undefined>(contentObj?.downloadAbled);
    const [instrumentIdSelected, setInstrumentIdSelected] = useState<number | undefined>(contentObj?.instrument?.id);
    const [editButtonClicked, setEditButtonClicked]     = useState<boolean>(false);
    const [cancelEditButtonClicked, setCancelEditButtonClicked] = useState<boolean>(false);
    const [deleteClicked, setDeleteClicked]             = useState<boolean>(false);

    const MemorizedContentPlayer = useMemo(() => React.memo(ContentPlayer), []);

    function handleFormatDate(date: any) {
        const originalDate   = new Date(date);
        const formattedDate  = format(originalDate, 'dd MMM yyyy HH:mm:ss');
        return formattedDate;
    }

    useEffect(() => {
        async function handleInstrumentsComboBox() {
            await dispatch(getInstruments());
        }
        handleInstrumentsComboBox();
    }, []);

    function centisecondsToTime(centiseconds: number | undefined) {
        if (typeof centiseconds === 'number') {
            const centisecondsInt = typeof centiseconds === 'string' ? parseInt(centiseconds) : centiseconds;

            if (!Number.isInteger(centisecondsInt)) {
                throw new Error('O valor deve ser um número inteiro.');
            }

            const totalSeconds          = Math.floor(centisecondsInt / 100);
            const minutes               = Math.floor(totalSeconds / 60);
            const seconds               = totalSeconds % 60;
            const remainingCentiseconds = centisecondsInt % 100;

            return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(remainingCentiseconds).padStart(2, '0')}`;
        }
        return undefined;
    }

    async function updateContent() {
        if (authHeaders) {
            const response = await dispatch(patchContent({
                authHeaders,
                urlParams: {
                    contentId:    contentObj?.id,
                    userNickname: contentObj?.user?.nickname,
                },
                body: {
                    title:         titleForm,
                    description:   descriptionForm,
                    downloadAbled: downloadAbledForm,
                    instrumentId:  instrumentIdSelected,
                },
            }));

            if (response.meta.requestStatus === 'fulfilled') {
                setEditButtonClicked(false);
            } else {
                response.payload.map((item: string) => { toast.error(item); });
            }
        }
    }

    async function deleteContent() {
        if (authHeaders) {
            const response = await dispatch(deleteUserContent({
                authHeaders,
                urlParams: {
                    contentId:    contentObj?.id,
                    userNickname: contentObj?.user?.nickname,
                },
            }));

            if (response.meta.requestStatus === 'fulfilled') {
                setEditButtonClicked(false);
                onCloseClick(false);
            } else {
                response.payload.map((item: string) => { toast.error(item); });
            }
        }
    }

    return (
        <>
            {(loadingUpdade || loadingDelete) && <LoadingModal />}

            {deleteClicked && (
                <YesNoDialogModal
                    message={'You will delete this content. Are You sure?'}
                    onYesClick={deleteContent}
                    onCloseClick={setDeleteClicked}
                />
            )}

            <Dialog
                open={true}
                onClose={() => onCloseClick(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        bgcolor: BG_DIALOG,
                        color:   TEXT_PRIMARY,
                        border:  BORDER_SUBTLE,
                    },
                }}
            >
                {/* ── DialogTitle ── */}
                <DialogTitle
                    sx={{
                        display:        'flex',
                        justifyContent: 'space-between',
                        alignItems:     'flex-start',
                        pb:             1,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        {editButtonClicked ? (
                            <TextField
                                size="small"
                                value={titleForm}
                                onChange={(e) => setTitleForm(e.target.value)}
                                sx={inputSx}
                            />
                        ) : (
                            <Typography variant="h6" fontWeight={700} color={TEXT_PRIMARY}>
                                {titleForm}
                            </Typography>
                        )}
                        <Typography variant="caption" color={TEXT_SECONDARY}>
                            {handleFormatDate('2024-06-11T20:22:27.532Z')}
                        </Typography>
                    </Box>

                    <IconButton
                        size="small"
                        onClick={() => onCloseClick(false)}
                        sx={{ color: TEXT_SECONDARY, ml: 1, alignSelf: 'flex-start' }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                {/* ── DialogContent ── */}
                <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Chips de metadata */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                        {editButtonClicked ? (
                            <FormControl size="small" sx={{ ...inputSx, minWidth: 140 }}>
                                <InputLabel>Instrument</InputLabel>
                                <Select
                                    value={instrumentIdSelected}
                                    label="Instrument"
                                    onChange={(e) => setInstrumentIdSelected(e.target.value as number)}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: { bgcolor: BG_INPUT, color: TEXT_PRIMARY },
                                        },
                                    }}
                                >
                                    {instrumentsForm && instrumentsForm.map((instrument: Instrument) => (
                                        <MenuItem key={instrument.id} value={instrument.id}>
                                            {instrument.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Chip
                                label={instrumentsForm.find((i: Instrument) => i.id === Number(instrumentIdSelected))?.name ?? 'other'}
                                size="small"
                                sx={{ bgcolor: BRAND_RED, color: '#fff', fontWeight: 600 }}
                            />
                        )}

                        <Chip
                            label={centisecondsToTime(contentObj?.file?.blob.metadata.centiseconds) ?? '—'}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: TEXT_PRIMARY }}
                        />

                        {editButtonClicked ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color={TEXT_SECONDARY}>
                                    Download:
                                </Typography>
                                <RadioGroup
                                    row
                                    value={downloadAbledForm ? 'true' : 'false'}
                                    onChange={(e) => setDownloadAbledForm(e.target.value === 'true')}
                                >
                                    <FormControlLabel
                                        value="true"
                                        control={
                                            <Radio
                                                size="small"
                                                sx={{
                                                    color: TEXT_SECONDARY,
                                                    '&.Mui-checked': { color: BRAND_RED },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" color={TEXT_PRIMARY}>
                                                On
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value="false"
                                        control={
                                            <Radio
                                                size="small"
                                                sx={{
                                                    color: TEXT_SECONDARY,
                                                    '&.Mui-checked': { color: BRAND_RED },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" color={TEXT_PRIMARY}>
                                                Off
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>
                            </Box>
                        ) : (
                            <Chip
                                icon={
                                    downloadAbledForm
                                        ? <LockOpenIcon fontSize="small" />
                                        : <LockIcon fontSize="small" />
                                }
                                label={downloadAbledForm ? 'Download On' : 'Download Off'}
                                size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: TEXT_SECONDARY }}
                            />
                        )}
                    </Stack>

                    {/* Descrição */}
                    {editButtonClicked ? (
                        <TextField
                            label="Description"
                            multiline
                            rows={3}
                            fullWidth
                            size="small"
                            value={descriptionForm}
                            onChange={(e) => setDescriptionForm(e.target.value)}
                            sx={inputSx}
                        />
                    ) : (
                        <Typography
                            variant="body2"
                            color={TEXT_SECONDARY}
                            sx={{ whiteSpace: 'pre-wrap', minHeight: 32, lineHeight: 1.6 }}
                        >
                            {descriptionForm || '—'}
                        </Typography>
                    )}

                    {/* Player */}
                    <Box sx={{ mt: 1 }}>
                        <MemorizedContentPlayer fileUrl={contentObj?.fileUrl} />
                    </Box>

                </DialogContent>

                {/* ── DialogActions ── */}
                <DialogActions
                    sx={{
                        borderTop:      BORDER_SUBTLE,
                        px:             2,
                        py:             1.5,
                        justifyContent: 'space-between',
                    }}
                >
                    <IconButton
                        onClick={() => setDeleteClicked(true)}
                        sx={{
                            color:    'rgba(255,100,100,0.8)',
                            '&:hover': { color: '#ff5555' },
                        }}
                    >
                        <DeleteOutlineIcon />
                    </IconButton>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {editButtonClicked ? (
                            <>
                                <Button
                                    onClick={() => setEditButtonClicked(false)}
                                    sx={{ color: TEXT_SECONDARY, textTransform: 'none' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={updateContent}
                                    sx={{
                                        bgcolor:         BRAND_RED,
                                        textTransform:   'none',
                                        fontWeight:      600,
                                        '&:hover': { bgcolor: '#a93226' },
                                    }}
                                >
                                    Confirm
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="outlined"
                                onClick={() => setEditButtonClicked(true)}
                                sx={{
                                    borderColor:   BRAND_RED,
                                    color:         BRAND_RED,
                                    textTransform: 'none',
                                    '&:hover': {
                                        bgcolor:     'rgba(192,57,43,0.08)',
                                        borderColor: BRAND_RED,
                                    },
                                }}
                            >
                                Edit
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}
